import loadable from '@loadable/component';
import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { Backdrop, Link, SHADOW_LEVEL1, styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { VFC } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { PopularProducts } from './PopularProducts';
import { PopularSearchQueries } from './PopularSearchQueries';
import { DESKTOP_BREAKPOINT } from '~/constants';
import { useNavigationContext } from '~/contexts/NavigationContext';
import { useWindowDimensionsContext } from '~/contexts/WindowDimensionsContext';
import { useMobileWidth, useTranslationByKey } from '~/hooks';
import { TranslationKeys } from '~/models';

const SearchResultsRoot = styled('div')(({ theme }) => ({
  background: theme.palette.common.white,
  boxShadow: SHADOW_LEVEL1,
  boxSizing: 'border-box',
  color: theme.palette.text.primary,
  display: 'flex',
  flexDirection: 'column',
  left: 0,
  padding: theme.spacing(14, 3, 3),
  position: 'absolute',
  top: 'var(--top-nav-height)',
  transition: 'top var(--top-nav-height-animation)',
  width: `calc(100% - var(--minibag-desktop-width, 0px))`,

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    padding: theme.spacing(6, 12),
  },
}));

const SearchResultsContentWrapper = styled('div')<{ ownerState: { innerHeight: number } }>(
  ({ ownerState: { innerHeight }, theme }) => {
    const rootPaddings = theme.spacing(17);

    return {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: `calc(${innerHeight}px - ${rootPaddings} - var(--top-nav-height))`,
      minHeight: 100,
      position: 'relative',
      width: '100%',

      [theme.breakpoints.down(DESKTOP_BREAKPOINT)]: {
        overflowY: 'auto',
        overscrollBehaviorY: 'contain',
      },

      [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
        display: 'grid',
        gridTemplateColumns: '30% 70%',
        gridTemplateRows: '29px auto',
      },
    };
  },
);

const SearchResultsHeader = styled('span')(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  margin: theme.spacing(1, 0, 4),

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    gridColumn: 1,
    gridRow: 1,
    margin: 0,
    marginTop: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const SearchResultsQuery = styled(Link)(({ theme }) => ({
  borderBottom: `2px solid ${theme.palette.text.primary}`,
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightBold,
  marginLeft: 3,
  paddingBottom: 3,
  textDecoration: 'none',

  '&:hover, &:focus, &:active': {
    borderBottom: `2px solid ${theme.palette.text.disabled}`,
    color: theme.palette.text.disabled,
  },
}));

const SearchResultsLeftSide = styled('div')(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    gridColumn: 1,
    gridRow: 2,
  },
}));

const SearchResultsLoader = styled('span')(({ theme }) => ({
  top: '60%',

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    top: '50%',
  },
}));

const LazyArticles = loadable(async () =>
  import('./Articles/Articles').then((mod) => mod.Articles),
) as VFC;

interface SearchResultsProps {
  formattedToken: string;
}

const SearchResultsContent: VFC<SearchResultsProps> = ({ formattedToken }) => {
  const { hideSearchResultsTab, isFetching, searchResults } = useNavigationContext();
  const { windowHeight } = useWindowDimensionsContext();
  const [innerHeight, setInnerHeight] = useState(windowHeight);
  const translations = useTranslationByKey();
  const isArticles = Boolean(searchResults?.Contents.length);
  const isMobile = useMobileWidth();
  const { getTestId } = useTestId();

  const RenderSearchResult = () =>
    useMemo(
      () => (
        <>
          {searchResults?.Query ? (
            <SearchResultsHeader data-testid={getTestId('search-results-header')}>
              {translations(TranslationKeys.SeeAllResultsFor)}
              <SearchResultsQuery
                href={`/search?query=${searchResults.Query}`}
                data-testid={getTestId('search-results-query')}
              >
                {searchResults.Query}
              </SearchResultsQuery>
            </SearchResultsHeader>
          ) : null}
          <PopularProducts formattedToken={formattedToken} />
          <SearchResultsLeftSide data-testid={getTestId('search-results-left-side')}>
            {!isMobile && <PopularSearchQueries />}
            {isArticles ? <LazyArticles /> : null}
          </SearchResultsLeftSide>
        </>
      ),
      [],
    );

  useEffect(() => {
    setInnerHeight(windowHeight);
  }, [windowHeight]);

  return (
    <SearchResultsRoot data-testid={getTestId('search-results-root')}>
      <SearchResultsContentWrapper
        ownerState={{ innerHeight }}
        data-testid={getTestId('search-results-content-wrapper')}
      >
        {isFetching ? (
          <SearchResultsLoader
            className="k-loading-image"
            data-testid={getTestId('search-results-loader')}
          />
        ) : (
          <RenderSearchResult />
        )}
      </SearchResultsContentWrapper>
      <Backdrop
        open
        sx={{ position: 'absolute', zIndex: '-1', width: '100vw', height: '100vh' }}
        onClick={hideSearchResultsTab}
      />
    </SearchResultsRoot>
  );
};

export const SearchResults: VFC<SearchResultsProps> = ({ formattedToken }) => {
  const { activeMenu } = useNavigationContext();
  const isMenuActive = activeMenu === ToggleMenuFlyout.MenuTypes.SearchResults;

  // no need to render hidden menu
  return isMenuActive ? <SearchResultsContent formattedToken={formattedToken} /> : null;
};
